import { useContext } from 'react';
import styled from '@emotion/styled/macro';
import {
	Accordion,
	AccordionControlProps,
	createStyles,
	Flex,
	Text,
	useMantineTheme,
} from '@mantine/core';
import { IconCopy, IconTrash } from '@tabler/icons-react';
import _ from 'lodash';
import { Chatbot } from '../../../../components/common/chatbot/Chatbot';
import { AppInfoContext } from '../../../../components/contexts/AppInfoContext';
import { Optional } from '../../../../types/common';
import { Echelon, ForcePackageData } from '../../../../types/force-packages';
import { findHighestAgentId } from '../../functions/findHighestAgentId';
import { findHighestEchelonId } from '../../functions/findHighestEchelonId';
import { updateEchelonAgentIds } from '../../functions/updateEchelonAgentIds';
import { updateEchelonIds } from '../../functions/updateEchelonIds';

interface AdditionalProps {
	echelon: Echelon;
	isTopLevel: boolean;
	settings: Optional<ForcePackageData, 'id'>;
	setSetting: (value: Echelon | null, duplicate?: boolean, changeTopLevelName?: boolean) => void;
	onNewSettingsApply: (json: ForcePackageData) => void;
}

type IAccordionsControl = AccordionControlProps & AdditionalProps;

const NestedAccordionsControl: React.FC<IAccordionsControl> = (props: IAccordionsControl) => {
	const { classes } = useStyles();
	const { isSuperuser, envVariables } = useContext(AppInfoContext);
	const { setSetting, onNewSettingsApply, isTopLevel, echelon, settings, ...nativeProps } = props;
	const theme = useMantineTheme();

	return (
		<AccordionControl
			{...nativeProps}
			sx={{ display: 'flex', alignItems: 'center' }}
			className={isTopLevel ? classes.topLevelAccordionControl : ''}
			autoCorrect="false"
			spellCheck="false"
			value={echelon.name}
		>
			<Text
				className={classes.accordionText}
				contentEditable={isSuperuser}
				suppressContentEditableWarning
				onKeyDown={(e) => {
					if (e.key === 'Enter') {
						e.preventDefault();
						e.stopPropagation();
						e.currentTarget.blur();
					}

					e.key = ''; // prevent Mantine from doing accessibility stuff on this
				}}
				onKeyUp={(e) => {
					e.preventDefault();
					e.stopPropagation();
				}}
				onClick={(e) => {
					e.preventDefault();
					e.stopPropagation();
				}}
				onBlur={(e) => {
					if (e.currentTarget.innerText !== echelon.name) {
						const newName = e.currentTarget.innerText;
						// timeout to dont stop animation
						setTimeout(() =>
							setSetting({ ...echelon, name: newName }, false, isTopLevel)
						);
					}
				}}
			>
				{echelon.name}
			</Text>

			{isTopLevel && isSuperuser && envVariables.IS_SAAS ? (
				<Chatbot
					settings={settings}
					origin="force-package"
					onApply={(json: string) => {
						onNewSettingsApply(JSON.parse(json));
					}}
				/>
			) : null}

			{!isTopLevel && isSuperuser ? (
				<Flex className={classes.buttonsContainer}>
					<IconWrapper
						title="Duplicate"
						onClick={(e) => {
							e.stopPropagation();
							let highestAgentId = findHighestAgentId(props.settings.fp.echelons);
							let highestEchelonId = findHighestEchelonId(props.settings.fp.echelons);
							const copiedEchelon = _.cloneDeep(echelon);
							const updatedEchelon = updateEchelonIds(
								updateEchelonAgentIds(copiedEchelon, highestAgentId)[0],
								highestEchelonId
							)[0];
							setSetting(updatedEchelon, true);
						}}
					>
						<IconCopy
							size="1.1rem"
							color={theme.colorScheme === 'dark' ? 'gray' : theme.colors.light[2]}
						></IconCopy>
					</IconWrapper>
					<IconWrapper
						title="Remove"
						onClick={(e) => {
							e.stopPropagation();
							setSetting(null, false);
						}}
					>
						<IconTrash
							size="1.1rem"
							color={theme.colorScheme === 'dark' ? 'gray' : theme.colors.light[2]}
						></IconTrash>
					</IconWrapper>
				</Flex>
			) : null}
		</AccordionControl>
	);
};

const useStyles = createStyles((theme) => ({
	buttonsContainer: {
		display: 'inline-flex !important',
		borderRight: `1px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[5] : theme.colors.light[5]}`,
		backgroundColor: 'inherit',
		height: '100%',
		paddingRight: '0.5rem',
	},

	topLevelAccordionControl: {
		backgroundColor: `${theme.colors.appMainColor[5]} !important`,
		color: `${theme.colors.accentRed[0]} !important`,

		':hover': {
			backgroundColor: `${theme.colors.appMainColor[6]} !important`,
			cursor: 'pointer !important',
		},
	},

	accordionText: {
		width: 'fit-content',
		display: 'inline',
		border: `0.125rem solid transparent`,
		padding: '.1rem 0.5rem',
		borderRadius: '0.25rem',

		'&:focus-visible': {
			outline: 'none',
			border: `0.125rem solid ${theme.colors.accentRed[6]}`,
		},

		'&:hover': theme.fn.hover({
			cursor: 'text',
		}),
	},
}));

const AccordionControl = styled(Accordion.Control)`
	border-bottom: none;
	background-color: ${({ theme }) =>
		theme.colorScheme === 'dark' ? theme.colors.dark[8] : theme.colors.light[1]};
	text-align: left;
	justify-content: space-between;
	cursor: pointer;

	& .mantine-Accordion-label {
		display: flex;
		flex-direction: row;
		justify-content: space-between;
		align-items: center;
	}

	& .mantine-Accordion-chevron {
		margin-left: 1rem;
	}
`;

const IconWrapper = styled(Flex)`
	color: white;
	border-radius: 0.25rem;
	height: 1.75rem;
	min-height: 1.75rem;
	width: 1.75rem;
	min-width: 1.75rem;
	border: 0.0625rem solid transparent;
	align-items: center;
	justify-content: center;

	& :active {
		margin-top: 0.0625rem;
	}
`;

export default NestedAccordionsControl;
